import React, { useRef } from "react";

import { useLocation, useSearchParams } from "react-router-dom";

import Typography from "@mui/material/Typography";

import IntegrationProviderIcon from "@app/analytics/components/IntegrationProviderIcon";
import ReportTitle from "@app/analytics/components/reports/ReportTitle";
import {
  HeaderDetailsContainer,
  HeaderTimeText,
  VerticalDivider,
} from "@app/analytics/components/reports/styles";
import {
  CHANNEL_OPTS_FOR_TOPIC_PROVIDER,
  MESSAGE_OPTS_FOR_TOPIC_PROVIDER,
} from "@app/analytics/constants/topics";
import useEditTopicReport from "@app/analytics/hooks/topics/useEditTopicReport";
import useTopicReport from "@app/analytics/TopicReport/hooks/useTopicReport";
import { getFormattedTopicDateRange } from "@app/analytics/TopicReport/utils";
import type { Topic } from "@app/analytics/types";
import { getRelativeDisplayDate } from "@app/analytics/utils";
import { getTotalWidth } from "@app/analytics/utils";
import useMeasure from "@app/common/hooks/useMeasure";
import useResponsiveView from "@app/common/hooks/useResponsiveView";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import { pluralizePhrase } from "@app/common/utils";
import * as TopicUtils from "@app/common/utils/topicUtils";

import { Link } from "@rui/Link";
import Stack, { DesktopOnlyRow, MobileOnlyRow, Row } from "@rui/Stack";
import { BackButton } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

import DownloadMenu from "./Download/DownloadMenu";
import ReportActionsMenu from "./ReportActionsMenu";
import SendVia from "./SendVia";
import ShareTopicReport from "./ShareTopicReport";
import {
  HeaderContainer,
  HeaderStatusChip,
  SourceCountChip,
  TopicReportChip,
} from "../styles";

const REPORT_TITLE_ID = "topic-report-title";
const TITLE_WIDTH_OFFSET = 44; // right padding + back button
const PROVIDER_ICON_SIZE = 54; // 42px for icon + 12px of spacing

const getMaxWidth = (containerWidth: number) => {
  const titleElement = document?.getElementById(REPORT_TITLE_ID);
  if (!titleElement) return 0;
  return Math.max(0, containerWidth - titleElement.offsetWidth);
};

const getReportHtmlTitle = (topic: Topic) => {
  let title = topic.title;

  const messageLabel =
    MESSAGE_OPTS_FOR_TOPIC_PROVIDER[topic.provider]?.sourceLabel;
  const channelLabel =
    CHANNEL_OPTS_FOR_TOPIC_PROVIDER[topic.provider]?.sourceLabel;

  if (messageLabel && topic.messageCount) {
    title += ` (from ${pluralizePhrase(topic.messageCount, messageLabel)})`;
  } else if (channelLabel && topic.channelCount) {
    title += ` (from ${pluralizePhrase(topic.channelCount, channelLabel)})`;
  }

  return title;
};

const Sources = ({
  topic,
  titleMaxWidth,
}: {
  topic: Topic;
  titleMaxWidth: number;
}) => {
  if (
    topic.provider === "unknown" ||
    (!topic.channelCount && !topic.messageCount)
  ) {
    return null;
  }

  const maxWidth = getMaxWidth(titleMaxWidth);

  if (maxWidth < 50) {
    // anything narrower than 50px and you start getting weird UI where both the title
    // and this text get truncated. also at 50px or smaller you can't even see the number
    // of sources anyway, since the text gets truncated in the middle of "from"
    return null;
  }

  const channelOpts = CHANNEL_OPTS_FOR_TOPIC_PROVIDER[topic.provider];
  const messageOpts = MESSAGE_OPTS_FOR_TOPIC_PROVIDER[topic.provider];

  return (
    <Typography
      variant="h2"
      className="header__sources"
      noWrap
      maxWidth={maxWidth}
      fontWeight={500}
    >
      from{" "}
      {messageOpts && topic.messageCount > 0
        ? pluralizePhrase(topic.messageCount, messageOpts?.sourceLabel ?? "")
        : pluralizePhrase(topic.channelCount, channelOpts?.sourceLabel ?? "")}
    </Typography>
  );
};

const TopicReportHeader = () => {
  const location = useLocation();
  const { inDesktopView } = useResponsiveView();
  const [containerRef, , { innerWidth }] = useMeasure();
  const actionsContentRef = useRef<HTMLDivElement | null>(null);

  const { topic, isSharedReport } = useTopicReport();
  const { editTopicReportTitle } = useEditTopicReport({ context: "report" });
  const [searchParams] = useSearchParams();

  // to appease the types
  if (!topic) return null;

  const handleEditTitle = (title: string) => {
    editTopicReportTitle.mutate(
      { id: topic.id, title },
      {
        onError: () => {
          toast.error(
            "Error updating report title. Please refresh the page or try again later."
          );
        },
      }
    );
  };

  const logoSrc = TopicUtils.getProviderLogoSrc(topic.provider);

  const titleMaxWidth =
    innerWidth -
    getTotalWidth(actionsContentRef.current) -
    TITLE_WIDTH_OFFSET -
    (logoSrc == null ? 0 : PROVIDER_ICON_SIZE);

  const backLocation = location.state?.from
    ? location.state.from
    : ROUTES.MEETING_REPORTS;

  return (
    <HeaderContainer ref={containerRef} provider={topic.provider}>
      <Row
        spacing={1.5}
        variant="left"
        alignItems={{ xxs: "start", sm: "center" }}
      >
        <DesktopOnlyRow sx={{ display: "contents" }}>
          <Link to={backLocation}>
            <BackButton className="header-button__back" />
          </Link>
        </DesktopOnlyRow>
        {logoSrc != null && <IntegrationProviderIcon src={logoSrc} />}
        <Stack spacing={0.5}>
          <Row
            overflow="hidden"
            flexShrink={1}
            alignItems="flex-end"
            title={getReportHtmlTitle(topic)}
          >
            <ReportTitle
              id={REPORT_TITLE_ID}
              canEdit={!isSharedReport && inDesktopView}
              value={topic.title}
              onEditChange={handleEditTitle}
              maxWidth={titleMaxWidth}
              sx={{ whiteSpace: { xxs: "wrap", sm: "nowrap" } }}
            />
            <DesktopOnlyRow display="contents">
              <Sources topic={topic} titleMaxWidth={titleMaxWidth} />
            </DesktopOnlyRow>
          </Row>
          <HeaderDetailsContainer maxWidth={`${titleMaxWidth}px`}>
            <HeaderTimeText className="header__date-and-time">
              {getFormattedTopicDateRange(topic)}
            </HeaderTimeText>
            <DesktopOnlyRow display="contents">
              <TooltipWrapper
                title={`Last updated: ${topic.lastMention.toFormat("DD h:mm a")}`}
                placement="bottom"
                enterDelay={1000}
              >
                <HeaderStatusChip status={topic.status}>
                  {topic.status === "inactive"
                    ? "Inactive"
                    : getRelativeDisplayDate(topic.lastMention)}
                </HeaderStatusChip>
              </TooltipWrapper>
            </DesktopOnlyRow>
            <VerticalDivider size="sm" />
            <TopicReportChip />
            <MobileOnlyRow display="contents">
              <VerticalDivider size="sm" />
              <SourceCountChip count={topic.messageCount} />
            </MobileOnlyRow>
          </HeaderDetailsContainer>
        </Stack>
      </Row>
      <Row
        variant="right"
        spacing={{ xxs: 0.5, sm: 1.5 }}
        flexGrow={0}
        ref={actionsContentRef}
      >
        {!isSharedReport && (
          <>
            <DesktopOnlyRow sx={{ display: "contents" }}>
              <DownloadMenu topic={topic} />
              <VerticalDivider size="md" />
            </DesktopOnlyRow>

            <ShareTopicReport
              initEntityEmail={searchParams.get("shareEmail")}
            />
            <DesktopOnlyRow sx={{ display: "contents" }}>
              <SendVia />
            </DesktopOnlyRow>
          </>
        )}
        <ReportActionsMenu topic={topic} shared={isSharedReport} />
      </Row>
    </HeaderContainer>
  );
};

export default TopicReportHeader;
